<template>
	<v-layout id="assetDetailsInfo" fill-height wrap>
		<v-flex xs12 sm6 class="info-panel" :class="$vuetify.breakpoint.name === 'xs' ? 'pr-0' : 'pr-2'">
			<v-card>
				<v-toolbar flat dense card>
					<v-toolbar-title class="subheading">Details</v-toolbar-title>
					<v-spacer></v-spacer>
					<v-btn icon @click="$emit('edit')"><v-icon>edit</v-icon></v-btn>
				</v-toolbar>
				<v-card-text>
					<div>
						<div class="text-xs-left details">
							<div>
								<strong>Asset No: </strong><v-chip label small>{{ asset.assetNo }}</v-chip>
							</div>
							<div v-if="category">
								<strong>Category: </strong><span>{{ category.name }}</span>
							</div>
							<div v-if="location">
								<strong>Location: </strong><span>{{ location.name }}</span>
							</div>
							<div>
								<strong>Assigned: </strong>
								<span v-if="asset.assignedTo">
									<router-link v-if="$_hasAccessToEmployee(loggedInEmployee, asset.assignedTo.id)" :to="{ name: 'employee', params: { id: asset.assignedTo.id } }"
										><v-icon small color="success">person</v-icon>{{ asset.assignedTo.firstName }} {{ asset.assignedTo.lastName }}</router-link
									>
									<span v-else> {{ asset.assignedTo.firstName }} {{ asset.assignedTo.lastName }} </span>
								</span>
								<span v-else>Not Assigned </span>
							</div>
						</div>
						<v-divider class="my-3" v-if="hasAnyDetails" />
						<div class="text-xs-left details">
							<div v-if="asset.description"><strong>Description: </strong>{{ asset.description }}</div>
							<div v-if="asset.serialNo"><strong>Serial No: </strong>{{ asset.serialNo }}</div>
							<div v-if="asset.modelNo"><strong>Model No: </strong>{{ asset.modelNo }}</div>
							<div v-if="asset.manufacturer"><strong>Manufacturer: </strong>{{ asset.manufacturer }}</div>
							<div v-if="asset.vendor"><strong>Vendor: </strong>{{ asset.vendor }}</div>
							<div v-if="asset.costPrice"><strong>Cost Price: </strong>{{ formattedCostPrice }}</div>
							<div v-if="asset.notes"><strong>Notes: </strong>{{ asset.notes }}</div>
							<div v-if="asset.purchasedDate"><strong>Purchase Date: </strong>{{ asset.purchasedDate }}</div>
							<div v-if="asset.warrantyExpirationDate"><strong>Warranty Expiration: </strong>{{ moment(asset.warrantyExpirationDate).format('DD MMM, YYYY') }}</div>
							<div v-if="asset.nextServiceDate">
								<strong>Next Service Date: </strong>{{ moment(asset.nextServiceDate).format('DD MMM, YYYY') }}
								<!-- <v-tooltip bottom>
									<v-btn slot="activator" icon flat small @click="$emit('service')"><v-icon small>build</v-icon></v-btn>
									<span>Record Service</span>
								</v-tooltip> -->
							</div>
						</div>
					</div>
				</v-card-text>
			</v-card>
			<v-card class="mt-4">
				<v-toolbar flat dense card>
					<v-toolbar-title class="subheading">Uploaded Files</v-toolbar-title>
					<v-spacer></v-spacer>
					<v-btn icon @click="showUploadDialog = true"><v-icon>backup</v-icon></v-btn>
				</v-toolbar>
				<v-card-text>
					<div v-if="asset.files && asset.files.length">
						<v-list v-if="asset.files && asset.files.length" class="items">
							<v-list-tile v-for="file in asset.files" @click="" :key="file.file.id">
								<v-list-tile-content>
									<v-list-tile-title>{{ file.file.fileName }}</v-list-tile-title>
									<v-list-tile-sub-title>{{ getFileSize(file.file.size) }}</v-list-tile-sub-title>
								</v-list-tile-content>
								<v-list-tile-action>
									<v-btn icon @click.stop="handleDownload(file.file)"><v-icon>get_app</v-icon></v-btn>
								</v-list-tile-action>
								<v-list-tile-action>
									<v-btn icon @click.stop="onClickDeleteFile(file.file)"><v-icon>delete</v-icon></v-btn>
								</v-list-tile-action>
							</v-list-tile>
						</v-list>
					</div>
					<div v-else class="grey--text">There are currently no files</div>
				</v-card-text>
				<file-upload v-if="showUploadDialog" :dialog="showUploadDialog" :loading="isUploading" title="Upload files for this asset" @onSecondaryAction="showUploadDialog = false" @onPrimaryAction="handleUpload" />

				<confirmation-dialog :dialog="showDeleteFileDialog" title="Confirm Delete File" text="Are you sure you want to delete this file?" :isConfirming="isDeletingFile" @onConfirm="handleDeleteFile" @onCancel="showDeleteFileDialog = false" />
			</v-card>
		</v-flex>
		<v-flex xs12 sm6 class="info-panel history-container" :class="$vuetify.breakpoint.name === 'xs' ? 'pl-0 mt-4' : 'pl-2'">
			<v-card>
				<v-toolbar flat dense card>
					<v-toolbar-title class="subheading"> Asset History</v-toolbar-title>
					<v-spacer></v-spacer>
				</v-toolbar>
				<v-card-text class="pa-0">
					<div v-if="actions.length" class="asset-history-container">
						<asset-details-action v-for="action in actions" :action="action" :key="action.id" />
					</div>
					<div v-else class="grey--text">There are no asset actions</div>
				</v-card-text>
			</v-card>
		</v-flex>
	</v-layout>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex';
	import AssetDetailsAction from '@components/assets/actions/AssetDetailsAction.vue';
	import moment from 'moment';
	import FileUpload from '@components/FileUpload.vue';
	import AssetServiceDialog from '@components/assets/AssetServiceDialog.vue';

	export default {
		name: 'AssetDetailsInfo',
		components: { AssetServiceDialog, FileUpload, AssetDetailsAction },
		props: {
			asset: {
				type: Object,
				required: true
			}
		},
		data() {
			return {
				showUploadDialog: false,
				showDeleteFileDialog: false,
				deletingFile: null,
				serviceDate: null
			};
		},
		computed: {
			moment() {
				return moment;
			},
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee',
				currentTenant: 'loggedInTenant',
				categories: 'assetConfiguration/categories',
				locations: 'assetConfiguration/locations',
				employees: 'employees/employees',
				isUploading: 'assets/isUploading',
				isDeletingFile: 'assets/isDeletingFile',
				isSaving: 'assets/isSaving'
			}),
			category() {
				return this.categories.find(x => x.id === this.asset.categoryId);
			},
			location() {
				return this.locations.find(x => x.id === this.asset.currentLocationId);
			},
			actions() {
				const createdAction = {
					id: this.asset.id,
					createdDateUTC: this.asset.createdDateUTC,
					createdByEmployee: this.employees.find(x => x.id === this.asset.createdByEmployeeId),
					action: 'Created'
				};

				if (!this.asset.actions) return [createdAction];

				let actions = createdAction.createdByEmployee ? [...this.asset.actions, createdAction] : [...this.asset.actions];

				actions.sort((a, b) => moment(b.createdDateUTC) - moment(a.createdDateUTC));

				return actions;
			},
			hasAnyDetails() {
				return (
					this.asset.description ||
					this.asset.serialNo ||
					this.asset.modelNo ||
					this.asset.manufacturer ||
					this.asset.vendor ||
					this.asset.costPrice ||
					this.asset.notes ||
					this.asset.purchasedDate ||
					this.asset.warrantyExpirationDate ||
					this.asset.nextServiceDate
				);
			},
			formattedCostPrice() {
				if (!this.asset.costPrice) return null;
				return '$' + this.asset.costPrice.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
			}
		},
		methods: {
			...mapActions({
				uploadFile: 'assets/uploadFiles',
				downloadFile: 'assets/downloadFile',
				deleteFile: 'assets/deleteFile',
				serviceAsset: 'assets/service'
			}),
			onClickDeleteFile(file) {
				this.showDeleteFileDialog = true;
				this.deletingFile = file;
			},
			async handleUpload(uploadFormData) {
				const result = await this.uploadFile({ assetId: this.asset.id, document: uploadFormData });

				if (result.isSuccess) {
					this.showUploadDialog = false;
				}
			},
			async handleDownload(file) {
				await this.downloadFile({ assetId: this.asset.id, fileId: file.id, fileName: file.fileName });
			},
			async handleDeleteFile() {
				const response = await this.deleteFile({ assetId: this.asset.id, fileId: this.deletingFile.id });

				if (response.isSuccess) {
					this.deletingFile = null;
					this.showDeleteFileDialog = false;
				}
			},
			getFileSize(bytes) {
				if (bytes < 100000) return `${Math.round(bytes / 1000)}kb`;
				else return `${Math.round(bytes / 1000000)}mb`;
			}
		}
	};
</script>

<style scoped>
	.details {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.asset-history-container {
		padding: 1.5rem;
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		max-height: 80vh;
		overflow-y: auto;
	}
</style>
